/**
 * ConfirmDialog — implements A6 (destructive & irreversible actions).
 *
 * Variants:
 *   default      → a normal confirmation
 *   destructive  → danger confirm action + the consequence stated in the body
 *   typed        → destructive + the user must type the exact token shown
 *
 * It also owns the in-flight state and the failure path, so callers stop
 * inventing their own (and stop using window.confirm).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  consequence,            // what will happen — mandatory for destructive
  impactPreview,          // optional node: counts, list, diff
  confirmLabel = "Conferma",
  cancelLabel = "Annulla",
  variant = "default",    // default | destructive | typed
  typedToken,             // required when variant === "typed"
  onConfirm,              // async () => void — throwing keeps the dialog open
  errorMessage,
  testId = "confirm-dialog",
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (open) { setTyped(""); setFailure(null); setBusy(false); }
  }, [open]);

  const isDestructive = variant === "destructive" || variant === "typed";
  const typedOk = variant !== "typed" || typed.trim() === (typedToken || "").trim();

  const confirm = async () => {
    if (!typedOk || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await onConfirm();
      setBusy(false);
      onOpenChange(false);
    } catch (err) {
      setBusy(false);
      setFailure(
        err?.response?.data?.detail || err?.message || errorMessage || "Operazione non riuscita",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDestructive && (
              <AlertTriangle className="w-4 h-4 text-danger shrink-0" aria-hidden="true" />
            )}
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription data-testid={`${testId}-description`}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {consequence && (
          <p
            data-testid={`${testId}-consequence`}
            className={`text-sm rounded-lg border px-3 py-2 ${isDestructive
              ? "border-danger-border bg-danger-surface text-danger"
              : "border-line-soft bg-surface-subtle text-content-secondary"}`}
          >
            {consequence}
          </p>
        )}

        {impactPreview && (
          <div
            data-testid={`${testId}-impact`}
            className="rounded-lg border border-line-soft bg-surface-subtle px-3 py-2 text-sm text-content-secondary"
          >
            {impactPreview}
          </div>
        )}

        {variant === "typed" && (
          <div className="space-y-1.5">
            <label htmlFor={`${testId}-typed`} className="block text-label uppercase text-content-muted">
              Per confermare digita <span className="font-mono text-content">{typedToken}</span>
            </label>
            <Input
              id={`${testId}-typed`}
              data-testid={`${testId}-typed-input`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>
        )}

        {failure && (
          <p role="alert" data-testid={`${testId}-error`} className="text-sm text-danger">
            {failure}
          </p>
        )}

        <DialogFooter>
          <Button
            data-testid={`${testId}-cancel`}
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            data-testid={`${testId}-confirm`}
            variant={isDestructive ? "danger" : "default"}
            onClick={confirm}
            disabled={busy || !typedOk}
            title={!typedOk ? "Digita il testo di conferma per procedere" : undefined}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {busy ? "Attendi…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDialog;
